import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useApp } from "@/contexts/AppContext";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Clock, Save } from "lucide-react";
import { toast } from "sonner";

const DAYS = ["sunday", "monday", "tuesday", "wednesday", "thursday", "friday", "saturday"];

export default function Availability() {
  const { t, lang } = useApp();
  const [weekdays, setWeekdays] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/availability").then((r) => {
      const w = r.data?.weekdays || {};
      // Ensure all 7 days exist
      const filled = {};
      DAYS.forEach((d) => {
        filled[d] = w[d] || { open: 17, close: 24, enabled: false };
      });
      setWeekdays(filled);
    });
  }, []);

  const update = (day, field, value) => {
    setWeekdays((curr) => ({ ...curr, [day]: { ...curr[day], [field]: value } }));
  };

  const onSave = async () => {
    setSaving(true);
    try {
      await api.put("/availability", { weekdays });
      toast.success(lang === "ar" ? "تم الحفظ" : "Saved");
    } catch (e) {
      toast.error("Failed");
    }
    setSaving(false);
  };

  if (!weekdays) return <div>Loading...</div>;

  return (
    <div className="space-y-6 max-w-2xl">
      <div>
        <p className="text-xs uppercase tracking-widest text-muted-foreground font-bold">{t.dashboard}</p>
        <h1 className="text-2xl sm:text-3xl font-bold mt-1 flex items-center gap-2">
          <Clock className="h-6 w-6 text-primary" />
          {t.weeklyAvailability}
        </h1>
        <p className="text-sm text-muted-foreground mt-2">
          {lang === "ar"
            ? "اضبط ساعات الحجز لكل يوم من أيام الأسبوع. ٢٤ تعني منتصف الليل."
            : "Set booking hours per weekday. 24 = midnight."}
        </p>
      </div>

      <div className="bg-surface border border-border rounded-xl divide-y divide-border">
        {DAYS.map((d) => {
          const cfg = weekdays[d];
          return (
            <div key={d} data-testid={`availability-row-${d}`} className="flex items-center gap-4 p-4 flex-wrap">
              <div className="w-24 font-semibold capitalize">{t[d]}</div>
              <Switch
                checked={!!cfg.enabled}
                onCheckedChange={(v) => update(d, "enabled", v)}
                data-testid={`availability-enabled-${d}`}
              />
              <div className="flex items-center gap-2 ms-auto">
                <label className="text-xs text-muted-foreground">{t.open}</label>
                <input
                  type="number"
                  min={0}
                  max={24}
                  value={cfg.open}
                  onChange={(e) => update(d, "open", Math.max(0, Math.min(24, Number(e.target.value))))}
                  data-testid={`availability-open-${d}`}
                  className="w-16 rounded-md border border-border bg-background px-2 py-1 text-sm text-center font-mono"
                  disabled={!cfg.enabled}
                />
                <span className="text-muted-foreground">:00</span>
                <label className="text-xs text-muted-foreground ms-2">{t.close}</label>
                <input
                  type="number"
                  min={0}
                  max={24}
                  value={cfg.close}
                  onChange={(e) => update(d, "close", Math.max(0, Math.min(24, Number(e.target.value))))}
                  data-testid={`availability-close-${d}`}
                  className="w-16 rounded-md border border-border bg-background px-2 py-1 text-sm text-center font-mono"
                  disabled={!cfg.enabled}
                />
                <span className="text-muted-foreground">:00</span>
              </div>
            </div>
          );
        })}
      </div>

      <Button onClick={onSave} disabled={saving} className="gap-2" size="lg" data-testid="availability-save">
        <Save className="h-4 w-4" />
        {saving ? "..." : t.saveChanges}
      </Button>
    </div>
  );
}
